import { z } from "zod";
/**
 * `GET /healthz` on `api` and on `game`.
 *
 * Phase-plan DoD: 200 with the build SHA when healthy, **503** when a
 * dependency the service cannot work without is down (api: Postgres or Redis;
 * game: `matchMaker.stats.fetchAll()`, which needs Redis).
 */
export const healthCheckSchema = z.object({
    ok: z.boolean(),
    latencyMs: z.number().optional(),
    error: z.string().optional(),
});
/**
 * **The single Postgres `LISTEN` connection's state — §16f A61.**
 *
 * `connected` = a live LISTEN is held. `reconnecting` = it is not, and a
 * connect attempt is in flight or scheduled. `down` = no listener is running at
 * all (stopped, or never started — which is the honest answer on any service
 * that does not hold one).
 *
 * > It is **reported, never fatal.** See `configListener` on the response.
 */
export const configListenerStateSchema = z.enum(["connected", "reconnecting", "down"]);
export const healthResponseSchema = z.object({
    ok: z.boolean(),
    service: z.string(),
    /** Short build SHA. `RAILWAY_GIT_COMMIT_SHA` on Railway, "local" otherwise. */
    commit: z.string(),
    uptimeSec: z.number(),
    checks: z.record(z.string(), healthCheckSchema),
    /**
     * **§16f A61 — the config LISTEN connection, reported and never fatal.**
     *
     * With one Postgres listener in the whole system (LOCKED rule 25), "is it
     * connected" is the difference between a lobby that is live and one that is
     * permanently 3 seconds stale — and before this it was diagnosable only by log
     * archaeology (Quinn's T-241 NB6: `config-notify.ts` documented `isConnected()`
     * as *"Reported by /healthz"* and `/healthz` did not report it).
     *
     * **This field must never change `ok`.** A22 item 3 made the 3 s cache TTL the
     * backstop precisely so freshness is unconditional rather than dependent on a
     * long-lived auxiliary connection, so a dead listener is a *degraded-but-
     * correct* state the architecture designed for. A `/healthz` that failed on it
     * would take the api out of rotation over three seconds of staleness — it
     * would manufacture the outage it was watching for.
     *
     * Optional because `game` and any future service answer `/healthz` without
     * holding a listener; absent and `"down"` are different statements.
     */
    configListener: configListenerStateSchema.optional(),
});
export const versionResponseSchema = z.object({
    service: z.string(),
    version: z.string(),
    commit: z.string(),
    env: z.string(),
    startedAt: z.string(),
});
//# sourceMappingURL=health.js.map